import _extends from "@babel/runtime/helpers/extends";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
/*
 *                     NOTICE
 *            COPYRIGHT 2018 Ab Initio
 *       UNPUBLISHED -- ALL RIGHTS RESERVED
 *
 * This program is confidential and a trade secret of Ab Initio.
 * The receipt or possession of this program does not convey any
 * rights to reproduce or disclose its contents, or to manufacture,
 * use or sell anything that it may describe, in whole or in part,
 * without the specific written consent of Ab Initio.
 */

import KendoBaseComponent from "abreact/incubator/ui/adapters/KendoBaseComponent";
import { ReactUtils } from "abreact/incubator/ui/utils/ReactUtils";
import { PopupManager } from "abreact/popup/PopupManager";
import { AbDialog } from "abreact/popup/AbDialog";
import * as AbWebResources from "abweb/generated/locale/AbWebResources";
import { LogAndRenderErrorPipeline } from "abweb/utils/error/LogAndRenderErrorPipeline";
import { KendoPreferences } from "abweb/utils/KendoPreferences";
import { StringUtils } from "abweb/utils/StringUtils";
import { TypeUtils } from "abweb/utils/TypeUtils";
import * as React from "react";
import * as ReactDOM from "react-dom";
export class TextEditor extends KendoBaseComponent {
  constructor(props) {
    super(props);
    _defineProperty(this, "defaultInputId", TypeUtils.generateUniqueId("kendo-text-editor"));
    _defineProperty(this, "displayFontJustifySize", void 0);
    _defineProperty(this, "onPotentialChange", () => {
      const value = this.widgetInstance.value();
      if (value !== this.initialNormalizedHtml) {
        this.props.onChange(value);
      }
      this.props.onUnsavedChange && this.props.onUnsavedChange();
    });
    _defineProperty(this, "unsubErrorListener", undefined);
    // If we want to track unsaved changes, these are the callbacks to do so
    _defineProperty(this, "onUnsavedChangeProps", {
      execute: this.onPotentialChange,
      keyup: this.onPotentialChange,
      keydown: this.onPotentialChange,
      paste: this.onPotentialChange
    });
    this.displayFontJustifySize = KendoPreferences.textEditDisplayFontJustifySize;
    this.onKendoChange = this.onKendoChange.bind(this);
  }

  // When the editor is inline, use a smaller subset of buttons,
  // but we have to add custom button to show the embedded dlg

  // Need 2 custom buttons to allow users to insert object links
  // TODO: Per DG-647 when we have a story on how to represent object links, we can add these back in
  // let title = AbWebResources.textEditor.insertObjectRef");
  //
  // let insertObjectLink = {name: "insertObjectLink",
  //   exec: (e: any) => this.showInsertObjectLinkDlg(),
  // };
  //
  // // TODO: Do not inject this button if there is nothing in the global object reference clipboard
  // title = AbWebResources.textEditor_pasteObjectRef();
  //
  // let pasteObjectLink = {name: "pasteObjectLink",
  //   exec: (e: any) => this.pasteObjectLink(),
  // };

  onKendoChange(event) {
    const originatingElement = event.sender;
    if (event.sender.value() == null) {
      event.sender.value(originatingElement.lastGoodValue);
    } else {
      originatingElement.lastGoodValue = event.sender.value();
    }
    this.onPotentialChange();
  }
  getStandardOptions(title) {
    const myTitle = title || AbWebResources.textEditor_showHtmlEditor();
    const resizable = !!this.props.resizable;
    return {
      resizable: {
        content: resizable,
        toolbar: false
      },
      encoded: false,
      serialization: {
        entities: false
      },
      tools: [{
        name: "popupEditor",
        exec: e => {
          this.showHtmlEditorDlg();
        },
        template: `<a href="" role="button" title="${myTitle}"
                      class="abui k-tool k-group-start k-group-end"
                      unselectable="on"
                      aria-pressed="false"><img class="ab-image" data-ab-image-key="absdk:edit" src="webapp/abweb/assets/absdk/images/edit.svg"></a>`
      }, {
        name: "bold"
      }, {
        name: "italic"
      }, {
        name: "underline"
      }, {
        name: "strikethrough"
      }, {
        name: "insertUnorderedList"
      }, {
        name: "insertOrderedList"
      }, {
        name: "foreColor"
      }, {
        name: "backColor"
      }]
    };
  }
  getStandardOptionsInPopup() {
    // Per MH-31777, the size of the HTML Editor is determined by stretching it to fit its container
    // so we neednt' make the Editor resizable
    const resizable = false;
    const tools = this.displayFontJustifySize ? [{
      name: "bold"
    }, {
      name: "italic"
    }, {
      name: "underline"
    }, {
      name: "strikethrough"
    }, {
      name: "justifyLeft"
    }, {
      name: "justifyCenter"
    }, {
      name: "justifyRight"
    }, {
      name: "justifyFull"
    }, {
      name: "insertUnorderedList"
    }, {
      name: "insertOrderedList"
    }, {
      name: "indent"
    }, {
      name: "outdent"
    }, {
      name: "createLink"
    }, {
      name: "unlink"
    }, {
      name: "fontName"
    }, {
      name: "fontSize"
    }, {
      name: "foreColor"
    }, {
      name: "backColor"
    }] : [{
      name: "bold"
    }, {
      name: "italic"
    }, {
      name: "underline"
    }, {
      name: "strikethrough"
    }, {
      name: "insertUnorderedList"
    }, {
      name: "insertOrderedList"
    }, {
      name: "createLink"
    }, {
      name: "unlink"
    }, {
      name: "foreColor"
    }, {
      name: "backColor"
    }];
    return {
      resizable: {
        content: resizable,
        toolbar: false // MH-20884: Toolbar in popup should wrap, not resize
      },
      encoded: false,
      serialization: {
        entities: false
      },
      tools
    };
  }
  showHtmlEditorDlg() {
    // Fire a change event first if necessary so that the dialog will see the latest content
    this.onPotentialChange();
    let updatedValue;
    const onChange = value => {
      updatedValue = value;
    };
    const saveChanges = () => {
      if (updatedValue) {
        this.currentValue = updatedValue;
        this.widgetInstance.value(updatedValue);
        this.props.onChange && this.props.onChange(updatedValue);
        this.widgetInstance.focus();
      }
      return Promise.resolve(true);
    };
    let resizable = true;
    if (typeof this.props.resizable === 'boolean') {
      resizable = this.props.resizable;
    } else if (this.props.resizable?.content === false) {
      resizable = false;
    }
    PopupManager.show(/*#__PURE__*/React.createElement(AbDialog, {
      title: AbWebResources.textEditorDlg_title(),
      className: "ab-text-editor-dialog",
      resizable: resizable,
      okButton: true,
      onOk: saveChanges,
      cancelButton: true,
      style: {
        minWidth: "400px",
        minHeight: 380,
        width: "600px"
      }
    }, /*#__PURE__*/React.createElement(TextEditor, _extends({}, this.props, {
      value: this.widgetInstance.value(),
      onChange: onChange,
      embeddedInPopup: true,
      key: "texteditor"
    }))));
  }
  componentDidMount() {
    // ABSDK-5306: kendo throws if you focus outside the HTML editor too quickly.  Suppress the error.
    this.unsubErrorListener = LogAndRenderErrorPipeline.pipeline.addErrorHandledListener(errCtx => {
      return errCtx.error.message === "Cannot read properties of null (reading 'nodeType')";
    });
    const domNode = ReactDOM.findDOMNode(this);
    const wrapper = domNode.appendChild(document.createElement("div"));
    wrapper.className = "ab-text-editor-wrapper";
    const textareaElement = document.createElement("textarea");
    if (this.props.embeddedInPopup) {
      wrapper.appendChild(textareaElement);
    }
    if (this.props.id) {
      wrapper.id = this.props.id;
    }

    // Create a Editor on text-area or 'wrapper' based on embeddedInPopup
    this.widgetInstance = this.props.embeddedInPopup ? new kendo.ui.Editor(textareaElement, {
      ...this.onUnsavedChangeProps,
      ...this.getStandardOptionsInPopup(),
      change: this.onKendoChange
    }) : new kendo.ui.Editor(wrapper, {
      ...this.onUnsavedChangeProps,
      ...this.getStandardOptions(),
      change: this.onKendoChange
    });
    const element = this.widgetInstance ? this.widgetInstance.element[0] : null;
    if (element) {
      element.id = this.props.id ? `${this.props.id}-input` : this.defaultInputId;
    }
    const theValue = this.currentValue ? this.currentValue : StringUtils.emptyIfNull(this.props.value);
    this.setInitialValue(theValue);
    super.componentDidMount();
  }
  componentWillUnmount() {
    if (this.unsubErrorListener) this.unsubErrorListener();
    super.componentWillUnmount();
  }
  componentWillReceiveProps(nextProps) {
    // Trim the children and widgetRef props, because the TextEditor shouldn't get them
    const {
      children,
      widgetRef,
      value,
      placeholder,
      disabled,
      ...rest
    } = nextProps;

    // Get the HTML element from the Kendo widget.  Set or clear the 'data-placeholder' attribute
    const element = this.widgetInstance ? this.widgetInstance.element[0] : null;
    if (element) {
      element.contentEditable = String(!disabled);
      if (placeholder) {
        element.setAttribute("data-placeholder", placeholder);
      } else {
        element.removeAttribute("data-placeholder");
      }
      element.id = this.props.id ? `${this.props.id}-input` : this.defaultInputId;
    }

    // Pass the remaining props to the TextEditor
    this.widgetInstance.setOptions(this.enrichProps(rest));
    // Set the text
    if (nextProps.value !== this.props.value) {
      const theValue = StringUtils.emptyIfNull(value);
      this.setInitialValue(theValue);
    }
  }
  setInitialValue(value) {
    const sanitizedHtml = StringUtils.sanitizeHtmlString(value);

    // MH-27912 on firefox seems to be caused by updating the kendo widget value from controlled component loop while the user is editing/typing
    // So we now compare the current kendo widget instance value to the new value, and don't update the kendo widget if the same
    // because of sanitization we need to be careful to compare equivalent things
    // for example sanitize will turn a trailing space into an &nbsp, and then kendo widget will  transform that back to a space
    if (StringUtils.sanitizeHtmlString(this.widgetInstance.value()) !== sanitizedHtml) {
      this.widgetInstance.value(sanitizedHtml);
    }

    // Because the Kendo editor normalizes the HTML, record the initial version
    // so we can tell if the dialog has changed.
    // unclear, relative to MH-27912 changes, if we need to do these all the time or not
    // in the interest of minimal code change because we don't understand this well, we will keep doing these.
    this.initialNormalizedHtml = this.widgetInstance.value();
    this.widgetInstance.lastGoodValue = value;
  }

  /** add event handlers to catch incremental changes */
  enrichProps(props) {
    if (this.props.onUnsavedChange) {
      return {
        ...this.onUnsavedChangeProps,
        ...props
      };
    } else {
      return props;
    }
  }
  render() {
    // TODO AndyA: how can we pass tabIndex through to the editor?
    const {
      tabIndex,
      ...editorProps
    } = ReactUtils.inputProps("ab-text-editor", this.props);
    return this.props.children || /*#__PURE__*/React.createElement("div", _extends({}, editorProps, {
      onFocus: this.props.onFocus,
      onBlur: this.props.onBlur
    }));
  }
}
//# sourceMappingURL=TextEditor.js.map
